import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import '@/index.css';
import { Toaster } from '@/components/ui/sonner';

// Components
import Navigation from '@/components/Navigation';

// Pages
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import Dashboard from '@/pages/Dashboard';
import GlobalFeed from '@/pages/GlobalFeed';
import ProfilePage from '@/pages/ProfilePage';
import VotingPage from '@/pages/VotingPage';
import LeaderboardPage from '@/pages/LeaderboardPage';
import AdminDashboard from '@/pages/AdminDashboard';
import NotificationsPage from '@/pages/NotificationsPage';
import AuthCallback from '@/pages/AuthCallback';
import CreatePost from '@/pages/CreatePost';
import EditProfile from '@/pages/EditProfile';
import JoinTalentShow from '@/pages/JoinTalentShow';
import JudgingPanel from '@/pages/JudgingPanel';
import EarnVotesPage from '@/pages/EarnVotesPage';
import BuyVotesPage from '@/pages/BuyVotesPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DebugPage from '@/pages/DebugPage';
import StatsPage from '@/pages/StatsPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data was passed from AuthCallback, use it directly
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      return;
    }

    // Otherwise, verify session via API (only on mount)
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) throw new Error('Not authenticated');
        const userData = await response.json();
        setIsAuthenticated(true);
        setUser(userData);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, []); // Only check auth once on mount

  if (isAuthenticated === null) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return isAuthenticated ? children : null;
}

// Layout component that conditionally shows navigation
function Layout({ children }) {
  const location = useLocation();
  
  // Pages where we don't show the navigation
  const noNavPages = ['/', '/login', '/register', '/join-talent-show'];
  const showNav = !noNavPages.includes(location.pathname);

  return (
    <>
      {showNav && <Navigation />}
      {children}
    </>
  );
}

// Router component to handle auth callback
function AppRouter() {
  const location = useLocation();
  
  // Check for session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Dashboard Redirect Component - redirects to user profile
  const DashboardRedirect = () => {
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    
    useEffect(() => {
      const fetchUser = async () => {
        try {
          const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
          if (response.ok) {
            const data = await response.json();
            setUser(data);
            navigate(`/profile/${data.user_id}`, { replace: true });
          } else {
            navigate('/feed', { replace: true });
          }
        } catch (error) {
          navigate('/feed', { replace: true });
        }
      };
      fetchUser();
    }, [navigate]);
    
    return <div className="min-h-screen flex items-center justify-center">Redirecting...</div>;
  };

  // Landing Page Redirect - redirects to feed (except admin goes to original landing)
  const LandingRedirect = () => {
    const [checking, setChecking] = useState(true);
    const navigate = useNavigate();
    
    useEffect(() => {
      const checkUser = async () => {
        try {
          const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
          if (response.ok) {
            const data = await response.json();
            if (data.is_admin) {
              setChecking(false); // Show original landing page for admin
              return;
            }
          }
          // Non-admin or not logged in - redirect to feed
          navigate('/feed', { replace: true });
        } catch (error) {
          navigate('/feed', { replace: true });
        }
      };
      checkUser();
    }, [navigate]);
    
    if (checking) {
      return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    }
    
    return <LandingPage />;
  };

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<LandingRedirect />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<Navigate to="/join-talent-show" replace />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/join-talent-show" element={<RegisterPage />} />
        <Route path="/Sign-up" element={<JoinTalentShow />} />
        <Route path="/dashboard" element={<DashboardRedirect />} />
        <Route path="/feed" element={<GlobalFeed />} />
        <Route path="/profile/:userId" element={<ProfilePage />} />
        <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
        <Route path="/create-post" element={<ProtectedRoute><CreatePost /></ProtectedRoute>} />
        <Route path="/voting" element={<ProtectedRoute><VotingPage /></ProtectedRoute>} />
        <Route path="/earn-votes" element={<ProtectedRoute><EarnVotesPage /></ProtectedRoute>} />
        <Route path="/buy-votes" element={<ProtectedRoute><BuyVotesPage /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/debug" element={<DebugPage />} />
        <Route path="/stats" element={<StatsPage />} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/judging-panel" element={<ProtectedRoute><JudgingPanel /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/feed" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
export { API, BACKEND_URL };
